import React from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Analiz Paneli'nin SVG istatistik karoları. HTML değil SVG: HaShimmerCard'ın PNG dışa
// aktarımı karttaki ilk SVG'yi rasterize eder — karo da indirilebilir olmalı (kullanıcı
// isteği: "her biri png olarak indirilebilecek istatistik kartları").
//
// dataviz: hero sayı + tek destek satırı; metin TEXT token'larında, seri rengi yalnız
// işarette (spark/yay). Tek seri → lejant yok, başlık kartta.
const VIEW_W = 280;
const VIEW_H = 120;

/** Büyük sayı + isteğe bağlı delta satırı + isteğe bağlı mini spark çizgisi. */
export function HaStatTile({ value, deltaText, deltaTone = 'neutral', sparkValues }) {
  const theme = useTheme();

  const toneColor =
    deltaTone === 'good' ? theme.colors.secondary.dark :
    deltaTone === 'bad' ? theme.colors.auxiliar.danger :
    theme.colors.text.muted;

  let sparkPath = null;
  if (Array.isArray(sparkValues) && sparkValues.length >= 2) {
    const min = Math.min(...sparkValues);
    const max = Math.max(...sparkValues);
    const span = max - min || 1;
    const x0 = 16; const x1 = VIEW_W - 16; const y0 = 108; const y1 = 84;
    sparkPath = sparkValues
      .map((v, i) => {
        const x = x0 + (i / (sparkValues.length - 1)) * (x1 - x0);
        const y = y0 - ((v - min) / span) * (y0 - y1);
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }

  return (
    <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} width="100%" height={VIEW_H} role="img">
      <text
        x={16} y={46} fontSize={34}
        fontFamily={theme.metrics.font.family} fontWeight={800}
        fill={theme.colors.text.primary} style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </text>
      {deltaText && (
        <text
          x={16} y={68} fontSize={12}
          fontFamily={theme.metrics.font.family} fontWeight={600} fill={toneColor}
        >
          {deltaText}
        </text>
      )}
      {sparkPath && (
        <path d={sparkPath} fill="none" stroke={theme.colors.primary.main} strokeWidth={2} strokeLinejoin="round" />
      )}
    </svg>
  );
}

/** Yarım daire gösterge: 0..1 oranı, hero sayıyla birlikte (kullanıcı isteği: "yarım daire"). */
export function HaGaugeTile({ ratio, valueText }) {
  const theme = useTheme();
  const { t } = useTranslation();

  const clamped = Math.max(0, Math.min(1, ratio ?? 0));
  const cx = VIEW_W / 2; const cy = 96; const r = 62;
  const angle = Math.PI * (1 - clamped); // 1 → 0 rad (sağ uç), 0 → π (sol uç)
  const ex = cx + r * Math.cos(angle);
  const ey = cy - r * Math.sin(angle);
  const largeArc = clamped > 0.5 ? 1 : 0;

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} width="100%" height={VIEW_H} role="img"
      aria-label={`${t('hrAnalytics.dashboard.gaugeAriaLabel')}: ${valueText}`}
    >
      {/* ray: soluk tam yarım daire; değer yayı üstüne çizilir (sequential tek ton) */}
      <path
        d={`M${cx - r},${cy} A${r},${r} 0 0 1 ${cx + r},${cy}`}
        fill="none" stroke={`${theme.colors.primary.main}22`} strokeWidth={12} strokeLinecap="round"
      />
      {clamped > 0.01 && (
        <path
          d={`M${cx - r},${cy} A${r},${r} 0 ${largeArc} 1 ${ex.toFixed(1)},${ey.toFixed(1)}`}
          fill="none" stroke={theme.colors.primary.main} strokeWidth={12} strokeLinecap="round"
        />
      )}
      <text
        x={cx} y={cy - 8} textAnchor="middle" fontSize={26}
        fontFamily={theme.metrics.font.family} fontWeight={800}
        fill={theme.colors.text.primary} style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {valueText}
      </text>
    </svg>
  );
}
